import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Lightning, Trophy, ChartBar } from "@phosphor-icons/react";
import { ToolCTA, InlineNewsletter } from "@/components/InlineCTA";
import SEO from "@/components/SEO";
import PublicNav from "@/components/PublicNav";
import { DTMBarChart, DTMLineChart, TOKENS } from "@/components/charts";

const API = process.env.REACT_APP_BACKEND_URL;

export default function RookieDraftGuide() {
  const [draftData, setDraftData] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/drafts/analysis`)
      .then(r => r.ok ? r.json() : null)
      .then(setDraftData)
      .catch(() => {});
  }, []);

  const rookie = draftData?.rookie;
  const topRookies = rookie?.top_36_adp?.slice(0, 24) || [];

  // Build ADP chart for top 12 rookies
  const adpChart = topRookies.slice(0, 12).map((p, i) => ({
    label: p.name.split(" ").pop(),
    value: p.adp,
    color: TOKENS.pos[p.position] || TOKENS.greyBar,
  }));

  // Positional distribution of top 24 rookies
  const posCounts = { QB: 0, RB: 0, WR: 0, TE: 0 };
  topRookies.forEach(p => { if (posCounts[p.position] !== undefined) posCounts[p.position]++; });
  const posChart = Object.entries(posCounts).filter(([, v]) => v > 0).map(([pos, count]) => ({
    label: pos, value: count, color: TOKENS.pos[pos],
  }));

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <SEO
        title="The Dynasty Rookie Draft Guide: Pick Values & What Actually Hits — Dynasty Trade Maker"
        description="Learn the real value of dynasty rookie draft picks and what they actually hit for. Data from 5,900+ real rookie drafts in championship-winning Sleeper leagues."
        path="/guide/dynasty-rookie-draft" image="https://dynastytrademaker.com/hero-images/guide-rookie-draft.jpg"
      />
      <PublicNav />
      <div className="max-w-3xl mx-auto px-4 py-8">
        <Link to="/guide" className="text-[10px] text-[#555] hover:text-[#a3a3a3] no-underline uppercase tracking-wider">Guide</Link>
        <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#ccff00] block mt-4 mb-2" data-testid="rookie-data-label">
          DATA FROM {rookie ? rookie.total_drafts.toLocaleString() : "5,900+"} REAL ROOKIE DRAFTS
        </span>
        <h1 className="font-heading text-3xl sm:text-4xl tracking-tight text-white uppercase mb-4" data-testid="rookie-title">
          THE ROOKIE DRAFT GUIDE: <span className="text-[#ccff00]">WHAT PICKS ARE WORTH</span>
        </h1>

        {/* Hero Image */}
        <div className="mb-6 border border-[#262626] rounded-sm overflow-hidden">
          <img src="/hero-images/guide-rookie-draft.jpg" alt="" className="w-full h-auto" loading="eager" />
        </div>

        <div className="text-sm text-[#a3a3a3] leading-relaxed space-y-6">
          <p>The rookie draft is a dynasty manager's favorite day of the offseason. It is also where a lot of value gets won and lost, because most people draft on hype and hope instead of on what actually works. We pulled real rookie-pick data from {rookie ? rookie.total_drafts.toLocaleString() : "5,900+"} real rookie drafts in championship-winning Sleeper leagues so you can walk into your draft knowing what each pick is actually worth.</p>

          <h2 className="font-heading text-2xl text-white uppercase mt-10 mb-3">HOW THE ROOKIE DRAFT WORKS</h2>
          <p>Every offseason, dynasty leagues hold a rookie draft where you pick from the incoming NFL rookie class. Draft order is usually tied to last year's standings, so the worst teams pick first. If you have been rebuilding, this is your payoff: early rookie picks are how you restock with young talent.</p>
          <p>Rookie picks are also a currency. They get traded constantly, both for other picks and for established players. So understanding pick value is not just about draft day. It is about every trade you make with a pick involved, which in dynasty is a lot of them.</p>

          <h2 className="font-heading text-2xl text-white uppercase mt-10 mb-3">REAL ROOKIE ADP FROM CHAMPION LEAGUES</h2>
          <p>These are not mock-draft projections. This is how real champion-league rookie drafts played out across {rookie ? rookie.total_drafts.toLocaleString() : "5,900+"} drafts.</p>

          {adpChart.length > 0 && (
            <DTMBarChart
              data={adpChart}
              title="TOP 12 ROOKIE ADP"
              subtitle={`Average draft position across ${rookie?.total_drafts?.toLocaleString() || "5,900+"} real rookie drafts.`}
              source={`${rookie?.total_drafts?.toLocaleString() || "5,900+"} rookie drafts from champion leagues`}
              layout="vertical"
              height={300}
              formatter={v => typeof v === "number" ? v.toFixed(1) : v}
              domain={[0, 14]}
            />
          )}

          {/* Full table */}
          {topRookies.length > 0 && (
            <div className="border border-[#262626] bg-[#171717] my-4 overflow-hidden" data-testid="rookie-adp-table">
              <div className="p-4 border-b border-[#262626]">
                <h3 className="text-[10px] font-bold uppercase tracking-wider text-[#ccff00] flex items-center gap-1.5"><ChartBar size={14} weight="fill" /> FULL ROOKIE ADP TOP 24</h3>
              </div>
              <table className="w-full text-xs">
                <thead><tr className="text-[10px] text-[#666] uppercase tracking-wider border-b border-[#262626]">
                  <th className="text-left py-2 px-4">Pick</th><th className="text-left py-2 px-4">Player</th><th className="text-left py-2 px-4">Pos</th><th className="text-right py-2 px-4">ADP</th><th className="text-right py-2 px-4 hidden sm:table-cell">Drafted</th>
                </tr></thead>
                <tbody>
                  {topRookies.map((p, i) => (
                    <tr key={i} className="border-b border-[#1a1a1a] hover:bg-[#1a1a1a]/50">
                      <td className="py-2 px-4 text-[#555] font-mono">{i + 1}</td>
                      <td className="py-2 px-4 text-white font-medium">{p.name}</td>
                      <td className="py-2 px-4"><span className="text-[10px] font-bold px-1.5 py-0.5 rounded-sm" style={{ color: TOKENS.pos[p.position] || "#888", backgroundColor: `${TOKENS.pos[p.position] || "#888"}15` }}>{p.position}</span></td>
                      <td className="py-2 px-4 text-right font-mono text-[#a3a3a3]">{p.adp}</td>
                      <td className="py-2 px-4 text-right font-mono text-[#555] hidden sm:table-cell">{p.times_drafted?.toLocaleString()}x</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Positional breakdown */}
          {posChart.length > 0 && (
            <DTMBarChart
              data={posChart}
              title="POSITIONS DRAFTED IN TOP 24"
              subtitle="Position breakdown of the top 24 rookie picks."
              layout="horizontal"
              height={180}
              formatter={v => v}
              showLabels
            />
          )}

          <h2 className="font-heading text-2xl text-white uppercase mt-10 mb-3">HOW TO VALUE ROOKIE PICKS IN TRADES</h2>

          <ToolCTA text="Trading a rookie pick? Check its real value first." subtext="Our calculator prices every pick from 1.01 to 4.12 in real-time." buttonText="VALUE ANY PICK" buttonLink="/trade-calculator" />
          <p>A few principles the data supports:</p>
          <div className="space-y-3 my-4">
            {[
              { title: "Early first-round picks carry a premium", desc: "The consensus 1.01 and 1.02 picks are genuine building blocks. That premium is usually worth paying in a rebuild where you need cornerstones." },
              { title: "Later picks are lottery tickets", desc: "Their value is in volume and upside, not certainty. Stack enough of them and you will hit on some, but do not overvalue any single late pick." },
              { title: "Future picks trade at a discount", desc: "You do not know the class strength or where the pick will land. That discount is a real opportunity for rebuilders who can afford to wait." },
            ].map(item => (
              <div key={item.title} className="border border-[#262626] bg-[#171717] p-4">
                <p className="text-sm text-white font-semibold mb-1">{item.title}</p>
                <p className="text-xs text-[#888]">{item.desc}</p>
              </div>
            ))}
          </div>

          <h2 className="font-heading text-2xl text-white uppercase mt-10 mb-3">HOW ROOKIES FIT YOUR BUILD</h2>

          <InlineNewsletter context="rookie" source="rookie-guide" />
          <p>Match your rookie strategy to your window. If you are contending, rookies are often better as trade chips than roster clogs. Package your picks to a rebuilder for a proven player who helps you win now. Remember, championship rosters averaged 27.4 years old and leaned on established production, not a stack of unproven rookies.</p>
          <p>If you are rebuilding, rookie picks are the core of your restock. Hit on a couple of early ones and you have the young cornerstones the champion data says you need, the concentrated core where the top 3 players hold about 36.5% of roster value.</p>

          {/* CTA */}
          <div className="border border-[#ccff00]/30 bg-[#ccff00]/5 p-6 text-center my-8" data-testid="rookie-cta">
            <h3 className="font-heading text-lg text-white uppercase mb-2">DRAFT WITH DATA, NOT HYPE</h3>
            <p className="text-xs text-[#888] mb-4">Before you make a pick-for-player trade, run it through our calculator so you know the pick is valued right.</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link to="/trade-calculator" className="h-9 px-5 bg-[#ccff00] text-black rounded-sm text-[11px] inline-flex items-center gap-1.5 no-underline font-bold hover:bg-[#b8e600] transition-colors">
                <Lightning size={12} weight="fill" /> VALUE ANY PICK TRADE, FREE
              </Link>
              <Link to="/rookie-rankings" className="text-[11px] text-[#ccff00] no-underline hover:underline flex items-center gap-1">See current rookie rankings <ArrowRight size={11} /></Link>
            </div>
          </div>

          {/* FAQ */}
          <h2 className="font-heading text-2xl text-white uppercase mt-10 mb-3">FREQUENTLY ASKED QUESTIONS</h2>
          <div className="space-y-3" itemScope itemType="https://schema.org/FAQPage">
            {[
              { q: "What is a rookie draft pick worth in dynasty?", a: "It depends on the slot and time of year. Early first-round picks carry the biggest premium because they produce startable dynasty assets most often." },
              { q: "When should I buy or sell rookie picks?", a: "Buy when they are cheapest (usually right after the NFL draft when hype is low). Sell when they peak (close to the dynasty rookie draft when demand spikes)." },
              { q: "Do early rookie picks really hit more often?", a: "Yes. Hit rate is highest at the top of the draft and drops as you go. The 1.01 is almost always a startable player. By the mid-second round, you are in true lottery territory." },
              { q: "Should I draft rookies or trade my picks?", a: "Depends on your window. Contenders often get more from trading picks for proven help, since champion rosters leaned on established production. Rebuilders should keep and use early picks to acquire cornerstones." },
              { q: "Why are future picks worth less than current picks?", a: "Uncertainty. You do not know the class strength or where the pick will land. That discount is a real opportunity for rebuilders who can afford to wait." },
            ].map((f, i) => (
              <div key={i} className="border border-[#262626] bg-[#171717] p-4" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                <p className="text-sm text-white font-semibold mb-1" itemProp="name">{f.q}</p>
                <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                  <p className="text-xs text-[#888]" itemProp="text">{f.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Related */}
        <div className="mt-8 space-y-2">
          <p className="text-[9px] font-bold uppercase tracking-wider text-[#555]">RELATED</p>
          {[
            { href: "/guide/dynasty-startup-draft", label: "Data-Backed Startup Draft Guide", desc: "What actually wins in startup drafts" },
            { href: "/tools/draft-archetype", label: "Draft Archetype Finder", desc: "Which champion build matches yours?" },
            { href: "/research/championship-report", label: "Championship Report", desc: "Every finding from 6,976 champions" },
          ].map(l => (
            <Link key={l.href} to={l.href} className="flex items-center justify-between p-3 border border-[#262626] bg-[#171717] hover:border-[#ccff00]/30 no-underline rounded-sm transition-colors">
              <div><p className="text-xs text-white font-semibold">{l.label}</p><p className="text-[10px] text-[#666]">{l.desc}</p></div>
              <ArrowRight size={12} className="text-[#555]" />
            </Link>
          ))}
        </div>

        <div className="mt-6 text-center text-[10px] text-[#444] space-x-3">
          <Link to="/guide" className="hover:text-[#a3a3a3] no-underline">Guides</Link>
          <Link to="/rankings" className="hover:text-[#a3a3a3] no-underline">Rankings</Link>
          <Link to="/trade-calculator" className="hover:text-[#a3a3a3] no-underline">Calculator</Link>
        </div>
      </div>
    </div>
  );
}
